import { useState } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { useAuth } from '../hooks/useAuth'
import { Eye, EyeOff, Lock, Mail, User } from 'lucide-react'
import LoadingSpinner from '../components/UI/LoadingSpinner'
import { TextField, Button } from '@anyemp/ui-kit'

const GoogleIcon = ({ className = 'w-5 h-5' }) => (
  <svg className={className} viewBox="0 0 48 48" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
    <path
      fill="currentColor"
      d="M44 24c0-1.34-.14-2.65-.4-3.92H24v7.42h11.24c-.48 2.58-1.94 4.77-4.13 6.25v5.2h6.68C41.7 35.34 44 30.13 44 24z M24 44c5.4 0 9.92-1.79 13.23-4.85l-6.68-5.2c-1.85 1.24-4.22 1.98-6.55 1.98-5.04 0-9.31-3.4-10.84-7.98H6.27v5.36C9.56 39.68 16.27 44 24 44z M13.16 27.95c-.39-1.17-.61-2.42-.61-3.7s.22-2.53.61-3.7v-5.36H6.27C4.83 17.85 4 20.81 4 24s.83 6.15 2.27 8.81l6.89-4.86z M24 12.5c2.84 0 5.39 1.02 7.4 2.93l5.55-5.55C33.91 6.46 29.39 4.5 24 4.5c-7.73 0-14.44 4.32-17.73 10.7l6.89 5.35C14.69 15.9 18.96 12.5 24 12.5z"
    />
  </svg>
)

const Login = () => {
  const [showPassword, setShowPassword] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isGoogleLoading, setIsGoogleLoading] = useState(false)
  const { login, loginWithGoogle } = useAuth()
  const navigate = useNavigate()
  const location = useLocation()

  // Obtain redirect URL after login
  const from = location.state?.from?.pathname || '/'

  const {
    register,
    handleSubmit,
    formState: { errors },
    setError
  } = useForm({
    defaultValues: {
      email: '',
      password: ''
    }
  })

  const onSubmit = async (data) => {
    try {
      setIsSubmitting(true)
      await login(data.email, data.password)
      navigate(from, { replace: true })
    } catch (error) {
      let errorMessage = 'Login failed';

      if (error.response?.data?.error) {
        errorMessage = error.response.data.error;
      } else if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.response?.data?.errors && error.response.data.errors.length > 0) {
        errorMessage = error.response.data.errors[0];
      } else if (error.message && error.message !== 'Request failed with status code 401') {
        errorMessage = error.message;
      }

      setError('root', {
        type: 'manual',
        message: errorMessage
      })
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleGoogleLogin = () => {
    try {
      setIsGoogleLoading(true)
      loginWithGoogle()
    } catch (error) {
      let errorMessage = 'Google login failed';

      if (error.response?.data?.error) {
        errorMessage = error.response.data.error;
      } else if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.response?.data?.errors && error.response.data.errors.length > 0) {
        errorMessage = error.response.data.errors[0];
      } else if (error.message && error.message !== 'Request failed with status code 401') {
        errorMessage = error.message;
      }

      setError('root', {
        type: 'manual',
        message: errorMessage
      })
      setIsGoogleLoading(false)
    }
  }

  return (
    <div className="w-full min-w-0 max-w-md space-y-8">
      <title>Login | Talent Service</title>
      {/* Header */}
      <div className="text-center">
        <div className="mx-auto h-16 w-16 bg-primary-600 rounded-full flex items-center justify-center">
          <User className="h-8 w-8 text-white" />
        </div>
        <h2 className="mt-6 text-3xl font-bold text-light-text-primary dark:text-dark-text-primary">
          Sign in
        </h2>
      </div>

      <div className="mt-8 space-y-6">
        {/* Google login — primary, on top */}
        <Button
          type="button"
          color="primary"
          onClick={handleGoogleLogin}
          disabled={isGoogleLoading}
          sx="w-full"
        >
          {isGoogleLoading ? (
            <span className="flex min-w-0 items-center justify-center">
              <LoadingSpinner size="sm" className="mr-2" />
              Connecting to Google…
            </span>
          ) : (
            <span className="flex min-w-0 items-center justify-center gap-2">
              <GoogleIcon className="w-5 h-5" />
              Sign in with Google
            </span>
          )}
        </Button>

        {/* Divider */}
        <div className="relative">
          <div className="absolute inset-0 flex items-center">
            <div className="w-full border-t border-light-border-primary dark:border-dark-border-primary"></div>
          </div>
          <div className="relative flex justify-center text-sm">
            <span className="px-2 bg-light-bg-primary dark:bg-dark-bg-secondary text-light-text-tertiary dark:text-dark-text-tertiary">
              Or sign in with email
            </span>
          </div>
        </div>

        {/* Email / password form */}
        <form className="space-y-4" onSubmit={handleSubmit(onSubmit)}>
          <TextField
            label="Email"
            id="email"
            type="email"
            autoComplete="email"
            fullWidth
            placeholder="Enter your email"
            startAdornment={<Mail className="h-5 w-5 text-light-text-tertiary dark:text-dark-text-tertiary" />}
            error={!!errors.email}
            helperText={errors.email?.message}
            {...register('email', {
              required: 'Email is required',
              pattern: {
                value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                message: 'Enter a valid email'
              }
            })}
          />

          <TextField
            label="Password"
            id="password"
            type={showPassword ? 'text' : 'password'}
            autoComplete="current-password"
            fullWidth
            placeholder="Enter your password"
            startAdornment={<Lock className="h-5 w-5 text-light-text-tertiary dark:text-dark-text-tertiary" />}
            endAdornment={(
              <button type="button" onClick={() => setShowPassword(!showPassword)}>
                {showPassword ? (
                  <EyeOff className="h-5 w-5 text-light-text-tertiary dark:text-dark-text-tertiary hover:text-light-text-primary dark:hover:text-dark-text-primary" />
                ) : (
                  <Eye className="h-5 w-5 text-light-text-tertiary dark:text-dark-text-tertiary hover:text-light-text-primary dark:hover:text-dark-text-primary" />
                )}
              </button>
            )}
            error={!!errors.password}
            helperText={errors.password?.message}
            {...register('password', {
              required: 'Password is required',
              minLength: {
                value: 6,
                message: 'Password must be at least 6 characters'
              }
            })}
          />

          {/* Session expired message */}
          {location.state?.message && !errors.root && (
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 dark:bg-yellow-900/30 dark:border-yellow-800">
              <p className="text-sm text-yellow-700 text-center dark:text-yellow-300">
                {location.state.message}
              </p>
            </div>
          )}

          {/* Form error */}
          {errors.root && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 dark:bg-red-900/30 dark:border-red-800">
              <p className="text-sm text-red-600 text-center dark:text-red-300">
                {errors.root.message}
              </p>
            </div>
          )}

          <Button
            type="submit"
            variant="outlined"
            color="neutral"
            disabled={isSubmitting}
            sx="w-full"
          >
            {isSubmitting ? (
              <span className="flex min-w-0 items-center justify-center">
                <LoadingSpinner size="sm" className="mr-2" />
                Signing in…
              </span>
            ) : (
              'Sign in'
            )}
          </Button>
        </form>
      </div>
    </div>
  )
}

export default Login
